import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateTaskTimestamp } from "../../repositories/SharedEqpRepository";

interface UpdateTaskTimestampParams {
  taskId: number;
  newTimestamp: Date;
}

/**
 * Shared mutation for correcting task timestamps.
 *
 * @param invalidateKeys - Explicit list of query key prefixes to invalidate on success.
 *   Each entry is passed to `invalidateQueries({ queryKey })` as a prefix match.
 *   Example: `[["roof-tree"], ["roof-details"]]`
 * @param procedureName - Optional override SP name (Caster uses a custom wrapper).
 */
export const useUpdateTaskTimestampMutation = (
  invalidateKeys: readonly (readonly string[])[],
  procedureName?: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpdateTaskTimestampParams) => {
      const resp = await updateTaskTimestamp(
        params.taskId,
        params.newTimestamp,
        procedureName,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to update task timestamp");

      return resp;
    },
    onSuccess: () => {
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [...key] });
      });
    },
  });
};
